import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Tag, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full bg-[#0A0E14] border border-white/15 px-3 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-[#00C8D7] transition-colors";

export default function PricingModal({ open, onClose, token }) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/pricing`).then((r) => setCfg(r.data)).catch(() => toast.error("Errore nel caricamento del listino."));
    }
  }, [open]);

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setRow = (list, i, k, v) =>
    setCfg((c) => ({ ...c, [list]: c[list].map((row, idx) => (idx === i ? { ...row, [k]: v } : row)) }));
  const addRow = (list) =>
    setCfg((c) => ({ ...c, [list]: [...c[list], { v: "", label: "", mult: 1 }] }));
  const removeRow = (list, i) =>
    setCfg((c) => ({ ...c, [list]: c[list].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setLoading(true);
    try {
      const payload = {
        base_price: parseFloat(cfg.base_price) || 0,
        price_per_cm3: parseFloat(cfg.price_per_cm3) || 0,
        currency: cfg.currency || "€",
        materials: cfg.materials
          .filter((m) => m.v && m.label)
          .map((m) => ({ v: m.v, label: m.label, mult: parseFloat(m.mult) || 1 })),
        qualities: cfg.qualities
          .filter((q) => q.v && q.label)
          .map((q) => ({ v: q.v, label: q.label, mult: parseFloat(q.mult) || 1 })),
      };
      await axios.put(`${API}/pricing`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Listino aggiornato.");
      onClose();
    } catch (err) {
      toast.error("Salvataggio non riuscito.");
    } finally {
      setLoading(false);
    }
  };

  const renderRowEditor = (list, title) => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono-tech text-xs uppercase tracking-[0.15em] text-slate-400">{title}</span>
        <button data-testid={`pricing-add-${list}`} onClick={() => addRow(list)} className="inline-flex items-center gap-1 text-[#00C8D7] text-xs hover:text-[#00C8D7]/80">
          <Plus size={14} /> Aggiungi
        </button>
      </div>
      <div className="space-y-2">
        {cfg[list].map((row, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center">
            <input data-testid={`${list}-v-${i}`} className={`${inputClass} col-span-3`} placeholder="Codice" value={row.v} onChange={(e) => setRow(list, i, "v", e.target.value)} />
            <input data-testid={`${list}-label-${i}`} className={`${inputClass} col-span-5`} placeholder="Etichetta" value={row.label} onChange={(e) => setRow(list, i, "label", e.target.value)} />
            <input data-testid={`${list}-mult-${i}`} type="number" step="0.1" className={`${inputClass} col-span-3`} placeholder="×" value={row.mult} onChange={(e) => setRow(list, i, "mult", e.target.value)} />
            <button data-testid={`${list}-remove-${i}`} onClick={() => removeRow(list, i)} className="col-span-1 text-slate-500 hover:text-red-400 flex justify-center">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm px-4 py-8 overflow-y-auto"
          onClick={onClose} data-testid="pricing-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.25 }} onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-[#111827] border border-white/10 my-auto"
          >
            <div className="flex items-center justify-between px-8 py-5 border-b border-white/10">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center bg-[#00C8D7]/10 text-[#00C8D7] border border-[#00C8D7]/30">
                  <Tag size={18} />
                </span>
                <h2 className="font-display font-bold text-xl tracking-tight">Listino prezzi</h2>
              </div>
              <button data-testid="pricing-close" onClick={onClose} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>

            {!cfg ? (
              <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#00C8D7]" size={26} /></div>
            ) : (
              <div className="px-8 py-6 space-y-6">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="font-mono-tech text-xs text-slate-400 block mb-1">Prezzo base</label>
                    <input data-testid="pricing-base" type="number" step="1" className={inputClass} value={cfg.base_price} onChange={(e) => setField("base_price", e.target.value)} />
                  </div>
                  <div>
                    <label className="font-mono-tech text-xs text-slate-400 block mb-1">Prezzo per cm³</label>
                    <input data-testid="pricing-percm3" type="number" step="0.01" className={inputClass} value={cfg.price_per_cm3} onChange={(e) => setField("price_per_cm3", e.target.value)} />
                  </div>
                  <div>
                    <label className="font-mono-tech text-xs text-slate-400 block mb-1">Valuta</label>
                    <input data-testid="pricing-currency" className={inputClass} value={cfg.currency} onChange={(e) => setField("currency", e.target.value)} />
                  </div>
                </div>

                {renderRowEditor("materials", "Materiali (× moltiplicatore)")}
                {renderRowEditor("qualities", "Qualità (× moltiplicatore)")}

                <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/10">
                  <button onClick={onClose} className="border border-white/15 text-slate-300 hover:text-white px-5 py-2.5 text-sm transition-colors">Annulla</button>
                  <button data-testid="pricing-save" onClick={save} disabled={loading} className="inline-flex items-center gap-2 bg-[#00C8D7] text-[#0A0E14] font-bold px-6 py-2.5 hover:bg-[#00C8D7]/85 transition-colors disabled:opacity-60">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : null} Salva listino
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
